import React, { useEffect, lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import RouteTitle from './components/RouteTitle';

// Eager: components rendered on every public navigation. Keeping the homepage
// in the main chunk avoids a Suspense flash on the LCP path.
import HomePage from './components/HomePage';
import HomeHeader from './components/HomeHeader';
import FloatingContactButton from './components/FloatingContactButton';
import StaticHeader from './components/StaticHeader';
import ProtectedRoute from './components/ProtectedRoute';
import PortalProtectedRoute from './components/PortalProtectedRoute';
import ImpersonationBanner from './components/ImpersonationBanner';
import { restoreUser } from './redux/middleware/users';

// Lazy: every other route. The public sub-pages are small enough that they
// could be eager, but lazy-loading them shaves the homepage's initial JS
// payload — visitors who never click past `/` never download the rest.
const AboutPage = lazy(() => import('./components/AboutPage'));
const AccessibilityPage = lazy(() => import('./components/AccessibilityPage'));
const AccessibilityStatementPage = lazy(() => import('./components/AccessibilityStatementPage'));
const NewClientsPage = lazy(() => import('./components/NewClientsPage'));
const ServicesPage = lazy(() => import('./components/ServicesPage'));
const ContactUsPage = lazy(() => import('./components/ContactUsPage'));
const DaycareInfoPage = lazy(() => import('./components/DaycareInfoPage'));
const BoardingInfoPage = lazy(() => import('./components/BoardingInfoPage'));
const TrainingInfoPage = lazy(() => import('./components/TrainingInfoPage'));
const BusServiceInfoPage = lazy(() => import('./components/BusServiceInfoPage'));
const PackHikesInfoPage = lazy(() => import('./components/PackHikesInfoPage'));
const PricesV2 = lazy(() => import('./components/PricesV2'));
const VaccinationsPage = lazy(() => import('./components/VaccinationsPage'));
const FAQInfoPage = lazy(() => import('./components/FaqInfoPage'));
const MeetAndGreetV1 = lazy(() => import('./components/MeetAndGreetV1'));
const JobsPage = lazy(() => import('./components/JobsPage'));

// Admin routes — heavy (booking wizard, calendar, instagram manager, etc.).
// Public visitors never touch these so they have no business sitting in the main chunk.
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const ManageAnimals = lazy(() => import('./components/ManageAnimals'));
const ManageBookings = lazy(() => import('./components/ManageBookings'));
const ManageClients = lazy(() => import('./components/ManageClients'));
const ClientProfile = lazy(() => import('./components/ClientProfile'));
const CreateBookingPage = lazy(() => import('./components/CreateBookingPage'));
const EditBookingPage = lazy(() => import('./components/EditBookingPage'));
const ManageLeads = lazy(() => import('./components/ManageLeads'));
const LeadPage = lazy(() => import('./components/LeadPage'));
const OutreachPage = lazy(() => import('./components/OutreachPage'));
const OutreachComposePage = lazy(() => import('./components/OutreachComposePage'));
const CheckInsPage = lazy(() => import('./components/CheckInsPage'));
const ChangeLogPage = lazy(() => import('./components/ChangeLogPage'));
const ManageEmployees = lazy(() => import('./components/ManageEmployees'));
const EmployeeProfile = lazy(() => import('./components/EmployeeProfile'));
const DogPhotoGallery = lazy(() => import('./components/DogPhotoGallery'));
const TodaysBookingsPage = lazy(() => import('./components/TodaysBookingsPage'));
const MediaLibrary = lazy(() => import('./components/MediaLibrary'));
const InstagramUpload = lazy(() => import('./components/InstagramUpload'));
const PostQueue = lazy(() => import('./components/PostQueue'));
const PostScheduler = lazy(() => import('./components/PostScheduler'));
const CalendarView = lazy(() => import('./components/CalendarView'));
const InstagramManager = lazy(() => import('./components/InstagramManager'));
const SiteImagePicker = lazy(() => import('./components/SiteImagePicker'));
const BookingRequestsPage = lazy(() => import('./components/BookingRequestsPage'));
const AdminBookingDetailPage = lazy(() => import('./components/AdminBookingDetailPage'));
const AdminVaccinationReview = lazy(() => import('./components/AdminVaccinationReview'));
const ClientVaccinationManager = lazy(() => import('./components/ClientVaccinationManager'));
const ManageVaccinations = lazy(() => import('./components/ManageVaccinations'));
const ManageVaccineRequirements = lazy(() => import('./components/ManageVaccineRequirements'));
const RevenueAnalytics = lazy(() => import('./components/RevenueAnalytics'));
const AdminProjections = lazy(() => import('./components/AdminProjections'));
const ManageBlockedDates = lazy(() => import('./components/ManageBlockedDates'));
const SuppressionList = lazy(() => import('./components/SuppressionList'));
const AdminBlogQueue = lazy(() => import('./components/AdminBlogQueue'));
const AdminUpdatesPage = lazy(() => import('./components/AdminUpdatesPage'));
const ShortcutSetupPage = lazy(() => import('./components/ShortcutSetupPage'));

// Employee routes
const EmployeeDashboard = lazy(() => import('./components/EmployeeDashboard'));
const EmployeeDogProfile = lazy(() => import('./components/EmployeeDogProfile'));
const EmployeeDogsPage = lazy(() => import('./components/EmployeeDogsPage'));
const EmployeeSchedulePage = lazy(() => import('./components/EmployeeSchedulePage'));
const EmployeeMorePage = lazy(() => import('./components/EmployeeMorePage'));
const EmployeeReportCardForm = lazy(() => import('./components/EmployeeReportCardForm'));
const EmployeeNav = lazy(() => import('./components/EmployeeNav'));

// Portal (client) routes
const PortalLoginPage = lazy(() => import('./components/PortalLoginPage'));
const PortalForgotPasswordPage = lazy(() => import('./components/PortalForgotPasswordPage'));
const SetPasswordPage = lazy(() => import('./components/SetPasswordPage'));
const PortalDashboard = lazy(() => import('./components/PortalDashboard'));
const BookingWizard = lazy(() => import('./components/BookingWizard'));
const BookingDetailPage = lazy(() => import('./components/BookingDetailPage'));
const BookingEditWizard = lazy(() => import('./components/BookingEditWizard'));
const PackageDetailPage = lazy(() => import('./components/PackageDetailPage'));
const AccountSettingsPage = lazy(() => import('./components/AccountSettingsPage'));
const VaccinationUpload = lazy(() => import('./components/VaccinationUpload'));
const OnboardingWizard = lazy(() => import('./components/OnboardingWizard'));

// Suspense fallback — minimal, theme-matching dark slab so the layout
// shift between eager and lazy routes is visually quiet.
const RouteFallback = () => (
  <div style={{ minHeight: '60vh', background: 'transparent' }} aria-hidden="true" />
);

function App() {

  const user = useSelector((state) => state.users.user);

  const location = useLocation(); 
  const dispatch = useDispatch();

  const isAdminPath = location.pathname.startsWith('/secure');
  const isEmployeePath = location.pathname.startsWith('/employee');
  const isPortalPath = location.pathname.startsWith('/portal');
  const isSetPasswordPath = location.pathname.startsWith('/set-password');

  const isAdminUser = user?.isAdmin;

  // Render correct header based on role and path
  const renderHeader = () => {
    if (isPortalPath || isSetPasswordPath) return null;    // Portal has its own layout (Phase 6)
    if (isEmployeePath) return null;  // No header for employee pages
    if (isAdminPath && isAdminUser) return <StaticHeader />;
    if (isAdminPath) return null;  // Hide header for non-admins on admin pages
    return <HomeHeader />;
  };


  useEffect(() => {
    dispatch(restoreUser());
  }, [dispatch]);

  // Dark background for admin/employee/portal pages (prevents white flash on iOS bounce)
  useEffect(() => {
    if (isPortalPath || isSetPasswordPath || isAdminPath || isEmployeePath) {
      document.documentElement.style.backgroundColor = '#141414';
      document.body.style.backgroundColor = '#141414';
    } else {
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
    }
  }, [isPortalPath, isSetPasswordPath, isAdminPath, isEmployeePath]);

  // Employee pages get their own PWA manifest (name "Dog Lodge Staff",
  // start_url /employee) so staff who "Add to Home Screen" from Android get
  // the right app identity. The admin manifest stays the default everywhere
  // else. iOS ignores manifest start_url and uses the install-time URL.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const target = isEmployeePath ? '/employee-manifest.json' : '/manifest.json';
    if (!link.getAttribute('href').endsWith(target)) {
      link.setAttribute('href', target);
    }
  }, [isEmployeePath]);

  // ✅ a11y — Stripe injects untitled <iframe> elements site-wide for its 3DS /
  // analytics infrastructure. We can't add the title attribute via Stripe's API,
  // so we observe and patch any iframe Stripe attaches. WCAG 4.1.2 / "frame-title".
  useEffect(() => {
    const titleStripeIframes = (root) => {
      root.querySelectorAll('iframe').forEach((f) => {
        if (f.title) return;
        const src = f.src || '';
        if (/stripe\.com|stripe\.network/.test(src)) {
          f.title = 'Stripe payment infrastructure (no user interaction required)';
        }
      });
    };
    titleStripeIframes(document);
    const observer = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.tagName === 'IFRAME') titleStripeIframes(n.parentNode || document);
          else if (n.querySelectorAll) titleStripeIframes(n);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);


  return (

    <div className={(isAdminPath || isEmployeePath) ? 'dark-admin' : ''}>

      <RouteTitle />

      {/* Red banner whenever this session is an admin viewing as an employee. */}
      <ImpersonationBanner />

      {/* WCAG 2.4.1 — Skip link. First focusable element on every page; hidden until
          focused. Wrapped in <nav aria-label="Skip"> so the skip link itself sits
          inside a landmark (IBM Equal Access aria_content_in_landmark). */}
      <nav aria-label="Skip">
        <a href="#main" className="skip-link">Skip to main content</a>
      </nav>

    {/* <FloatingCreateUser /> */}

{/* // Header */}
{renderHeader()}

{/* // Floating Contact Button (hide for admin and employee) */}
{!isAdminPath && !isEmployeePath && !isPortalPath && !isSetPasswordPath && <FloatingContactButton />}

{/* Employee bottom tab bar — the portal's only persistent chrome. */}
{isEmployeePath && user && (user.isEmployee || user.isAdmin) && (
  <Suspense fallback={null}>
    <EmployeeNav />
  </Suspense>
)}



{/* // Logout, Admin Home, and Employee Home buttons are now in the StaticHeader menu */}




      <main id="main" tabIndex="-1">
      <Suspense fallback={<RouteFallback />}>
      <Switch>



        <Route path="/about" exact>
          <AboutPage />
        </Route>

        <Route path="/accessibility" exact>
          <AccessibilityPage />
        </Route>

        <Route path="/accessibility-statement" exact>
          <AccessibilityStatementPage />
        </Route>

        <Route path="/new-clients" exact>
          <NewClientsPage />
        </Route>

        <Route path="/contact" exact>
          <ContactUsPage />
        </Route>

        <Route path="/services" exact>
          <ServicesPage />
        </Route>

        <Route path="/daycare-service-info" exact>
          <DaycareInfoPage />
        </Route>

        <Route path="/boarding-service-info" exact>
          <BoardingInfoPage />
        </Route>


        <Route path="/boarding-training-service-info" exact>
          <TrainingInfoPage />
        </Route>

        <Route path="/bus-service-info" exact>
          <BusServiceInfoPage />
        </Route>

        <Route path="/pack-hikes-service-info" exact>
          <PackHikesInfoPage />
        </Route>

        <Redirect from="/treat-times-service-info" to="/prices" exact />

        <Route path="/prices" exact>
          <PricesV2 />
        </Route>

        <Route path="/vaccinations-info" exact>
          <VaccinationsPage />
        </Route>

        <Route path="/faqs" exact>
          <FAQInfoPage />
        </Route>

        <Route path="/jobs" exact>
          <JobsPage />
        </Route>

        <Route path="/meet-and-greet" exact>
          <MeetAndGreetV1 />
        </Route>

        <Route path="/set-password" component={SetPasswordPage} /> 

        {/* Admin routes are protected */}
        <ProtectedRoute path="/secure" exact component={AdminDashboard} requiredRole="admin"/>
        <ProtectedRoute path="/secure/clients" exact component={ManageClients} requiredRole="admin"/>
        <ProtectedRoute path="/secure/animals" exact component={ManageAnimals} requiredRole="admin"/>
        <ProtectedRoute path="/secure/booking-requests" exact component={BookingRequestsPage} requiredRole="admin"/>
        <ProtectedRoute path="/secure/vaccinations" exact component={ManageVaccinations} requiredRole="admin"/>
        <ProtectedRoute path="/secure/vaccination-requirements" exact component={ManageVaccineRequirements} requiredRole="admin"/>
        <ProtectedRoute path="/secure/vaccination-reviews" exact component={AdminVaccinationReview} requiredRole="admin"/>
        <ProtectedRoute path="/secure/bookings/:id" exact component={AdminBookingDetailPage} requiredRole="admin"/>
        <ProtectedRoute path="/secure/bookings" exact component={ManageBookings} requiredRole="admin"/>
        <ProtectedRoute path="/secure/blocked-dates" exact component={ManageBlockedDates} requiredRole="admin"/>
        <ProtectedRoute path="/secure/suppressions" exact component={SuppressionList} requiredRole="admin"/>
        <ProtectedRoute path="/secure/employees" exact component={ManageEmployees} requiredRole="admin"/>
        <ProtectedRoute path="/secure/dog-photos" exact component={DogPhotoGallery} requiredRole="admin"/>
        <ProtectedRoute path="/secure/clients/:clientId/vaccinations" exact component={ClientVaccinationManager} requiredRole="admin"/>
        <ProtectedRoute path="/secure/clients/:clientId/bookings/:bookingId/edit" exact component={EditBookingPage} requiredRole="admin"/>
        <ProtectedRoute path="/secure/clients/:clientId/create-booking" exact component={CreateBookingPage} requiredRole="admin"/>
        <ProtectedRoute path="/secure/clients/:clientId" exact component={ClientProfile} requiredRole="admin"/>
        <ProtectedRoute path="/secure/leads" exact component={ManageLeads} requiredRole="admin"/>
        <ProtectedRoute path="/secure/leads/:leadId" exact component={LeadPage} requiredRole="admin"/>
        <ProtectedRoute path="/secure/outreach" exact component={OutreachPage} requiredRole="admin"/>
        <ProtectedRoute path="/secure/outreach/compose" exact component={OutreachComposePage} requiredRole="admin"/>
        <ProtectedRoute path="/secure/check-ins" exact component={CheckInsPage} requiredRole="admin"/>
        <ProtectedRoute path="/secure/change-log" exact component={ChangeLogPage} requiredRole="admin"/>
        <ProtectedRoute path="/secure/employees/:employeeId" exact component={EmployeeProfile} requiredRole="admin"/>


        <ProtectedRoute path="/secure/updates" exact component={AdminUpdatesPage} requiredRole="admin" />
        <ProtectedRoute path="/secure/setup-shortcut" exact component={ShortcutSetupPage} requiredRole="admin" />
        <ProtectedRoute path="/secure/today" exact component={TodaysBookingsPage} requiredRole="admin" />
        <ProtectedRoute path="/secure/revenue" exact component={RevenueAnalytics} requiredRole="admin" />
        <ProtectedRoute path="/secure/projections" exact component={AdminProjections} requiredRole="admin" />
        <ProtectedRoute path="/secure/admin/blog" exact component={AdminBlogQueue} requiredRole="admin" />

        {/* Instagram routes - employee access */}
        <ProtectedRoute path="/secure/media-library" exact component={MediaLibrary} requiredRole="employee"/>
        <ProtectedRoute path="/secure/instagram-upload" exact component={InstagramUpload} requiredRole="employee"/>
        <ProtectedRoute path="/secure/post-queue" exact component={InstagramManager} requiredRole="employee"/>
        <ProtectedRoute path="/secure/site-gallery" exact component={SiteImagePicker} requiredRole="employee"/>
        <ProtectedRoute path="/secure/post-queue-old" exact component={PostQueue} requiredRole="employee"/>
        <ProtectedRoute path="/secure/post-scheduler/:id" exact component={PostScheduler} requiredRole="employee"/>
        <ProtectedRoute path="/secure/post-scheduler" exact component={PostScheduler} requiredRole="employee"/>
        <ProtectedRoute path="/secure/post-calendar" exact component={CalendarView} requiredRole="employee"/>



        {/* Employee routes are protected */}
        <ProtectedRoute path="/employee" exact component={EmployeeDashboard} requiredRole="employee"/>
        <ProtectedRoute path="/employee/dogs" exact component={EmployeeDogsPage} requiredRole="employee"/>
        <ProtectedRoute path="/employee/schedule" exact component={EmployeeSchedulePage} requiredRole="employee"/>
        <ProtectedRoute path="/employee/me" exact component={EmployeeMorePage} requiredRole="employee"/>
        <ProtectedRoute path="/employee/dog/:petId/report-card" exact component={EmployeeReportCardForm} requiredRole="employee"/>
        <ProtectedRoute path="/employee/dog/:petId" exact component={EmployeeDogProfile} requiredRole="employee"/>

        {/* Portal routes (client-facing) */}
        <Route path="/portal/login" exact component={PortalLoginPage} />
        <Route path="/portal/forgot-password" exact component={PortalForgotPasswordPage} />
        <PortalProtectedRoute path="/portal/onboarding" exact component={OnboardingWizard} skipOnboardingCheck />
        <PortalProtectedRoute path="/portal/bookings/:id/edit" exact component={BookingEditWizard} />
        <PortalProtectedRoute path="/portal/bookings/:id" exact component={BookingDetailPage} />
        <PortalProtectedRoute path="/portal/book" exact component={BookingWizard} />
        <PortalProtectedRoute path="/portal/pets/:petId/vaccinations" exact component={VaccinationUpload} skipOnboardingCheck />
        <PortalProtectedRoute path="/portal/packages/:id" exact component={PackageDetailPage} />
        <PortalProtectedRoute path="/portal/settings" exact component={AccountSettingsPage} />
        <PortalProtectedRoute path="/portal" exact component={PortalDashboard} />

        {/* /blog and /blog/:slug were removed 2026-05-09 — backend 301-redirects
            those URLs to / before the SPA gets a chance to render. */}

        <Route path="/" exact>
          <HomePage />
        </Route>


      </Switch>
      </Suspense>
    </main>
    </div>
  );
}

export default App;
